import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {Col, Label, Row} from 'reactstrap';
import {AvField, AvForm, AvGroup, AvRadio, AvRadioGroup} from 'availity-reactstrap-validation';
import {IRootState} from 'app/shared/reducers';
import {setRegistrationData} from 'app/entities/registration/registration.reducer';
import 'app/entities/registration/registration.scss';
import {ContactRelation} from 'app/shared/model/enumerations/contact-relation.model';
import {IMMamaSettings} from 'app/shared/model/mmama-settings-model';
import RegistrationButtons from './registration-buttons';
import moment from "moment";

interface IPatientInfoProps extends StateProps, DispatchProps {
  handlePreviousStep: () => void;
  handleNextStep: () => void;
  currentStep: number;
  countrySettings: IMMamaSettings | null;
}

const PatientInfo: React.FC<IPatientInfoProps> = (props: IPatientInfoProps) => {
  const {patient, patientCase, locationType, countrySettings} = props;
  const [ageInput, setAgeInput] = useState('');
  const phoneMaxLength = countrySettings?.phoneNumberMaxLength || 15;
  const MIN_AGE = 9
  const MAX_AGE = 80

  // Calculate age from birthDate when component loads
  useEffect(() => {
    if (patient?.birthDate) {
      const birthDate = new Date(patient.birthDate);
      const currentDate = new Date();
      const age = currentDate.getFullYear() - birthDate.getFullYear();
      setAgeInput(age.toString());
    }
  }, [patient?.birthDate]);


  const handlePatientCaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;
    props.setRegistrationData({patientCase: {...patientCase, [name]: value}});
  };

  const handlePatientChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = e.target;

    if (name === 'age') {
      setAgeInput(value);
      // Convert age to birth date by subtracting from current date
      const age = parseInt(value, 10);
      if (age && age >= MIN_AGE && age <= MAX_AGE) {
        const formattedBirthDate = moment().subtract(age, 'years').format('YYYY-MM-DD');
        props.setRegistrationData({patient: {...patient, birthDate: formattedBirthDate}});
      }
    } else {
      props.setRegistrationData({patient: {...patient, [name]: value}});
    }
  };

  const handleRelationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedRelation = e.target.value as ContactRelation;
    props.setRegistrationData({patientCase: {...patientCase, primaryContactType: selectedRelation}});
  };

  return (
    <div>
      <h2 className="mb-4">Who is calling and how can you be reached?</h2>
      <AvForm onValidSubmit={props.handleNextStep}>
        {/* Contact Relation Section */}
        <AvRadioGroup name="radio-button-group" required value={patientCase?.primaryContactType}
                      errorMessage="Please select an option">
          <Row className="mb-4">
            {Object.values(ContactRelation).map((relation, index) => (
              <Col md="3" key={index}>
                <Label className="radio-button-label">
                  <AvRadio name="primaryContactType" value={relation} onChange={handleRelationChange}
                           className="radio-button-input"/>
                  {relation === ContactRelation.PATIENT ? 'MOTHER' : relation}
                </Label>
              </Col>
            ))}
          </Row>
        </AvRadioGroup>
        {patientCase?.primaryContactType && patientCase?.primaryContactType !== ContactRelation.PATIENT && (
          <>
            <Row className="mb-4">
              <Col md="12">
                <AvGroup>
                  <Label for="contactName">Contact Name</Label>
                  <AvField
                    type="text"
                    name="primaryContactName"
                    id="contactName"
                    value={patientCase?.primaryContactName || ''}
                    onChange={handlePatientCaseChange}
                    placeholder="Add the contact's full name"
                    required
                  />
                </AvGroup>
              </Col>
              <Col md="12">
                <AvGroup>
                  <Label for="contactPhone">Contact Number</Label>
                  <AvField
                    type="number"
                    name="primaryContactNumber"
                    id="contactPhone"
                    value={patientCase?.primaryContactNumber || ''}
                    onChange={handlePatientCaseChange}
                    placeholder="Add the contact's phone number"
                    required
                    validate={{
                      maxLength: {
                        value: phoneMaxLength,
                        errorMessage: `This field should be at most ${phoneMaxLength} characters long`
                      },
                    }}
                  />
                </AvGroup>
              </Col>
              <Col md="12">
                <AvGroup>
                  <Label for="secondaryContactNumber">Secondary Contact Number</Label>
                  <AvField
                    type="number"
                    name="secondaryContactNumber"
                    id="secondaryContactNumber"
                    value={patientCase?.secondaryContactNumber || ''}
                    onChange={handlePatientCaseChange}
                    placeholder="Add the contact's secondary phone number"
                    validate={{
                      maxLength: {
                        value: phoneMaxLength,
                        errorMessage: `This field should be at most ${phoneMaxLength} characters long`
                      },
                    }}
                  />
                </AvGroup>
              </Col>
            </Row>
          </>
        )}

        <h2 className="mb-4">Who is the mother?</h2>
        {/* Patient Information Section */}
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="firstName">First Name</Label>
              <AvField
                type="text"
                name="firstName"
                id="firstName"
                value={patient?.firstName || ''}
                onChange={handlePatientChange}
                placeholder="Add the mother's first name"
                required
              />
            </AvGroup>
          </Col>
          <Col md="12">
            <AvGroup>
              <Label for="lastName">Last Name</Label>
              <AvField
                type="text"
                name="lastName"
                id="lastName"
                value={patient?.lastName || ''}
                onChange={handlePatientChange}
                placeholder="Add the mother's last name"
                required
              />
            </AvGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="contactPhone">Contact Phone</Label>
              <AvField
                type="number"
                name="phoneNumber"
                id="contactPhone"
                value={patient?.phoneNumber || ''}
                onChange={handlePatientChange}
                placeholder="Add the mother's phone number"
                required
                validate={{
                  maxLength: {
                    value: phoneMaxLength,
                    errorMessage: `This field should be at most ${phoneMaxLength} characters long`
                  },
                }}
              />
            </AvGroup>
          </Col>
          <Col md="12">
            <AvGroup>
              <Label for="secondaryContactNumber">Secondary Contact Number</Label>
              <AvField
                type="number"
                name="secondaryPhoneNumber"
                id="secondaryContactNumber"
                value={patient?.secondaryPhoneNumber || ''}
                onChange={handlePatientChange}
                placeholder="Add the mother's secondary phone number if available"
                validate={{
                  maxLength: {
                    value: phoneMaxLength,
                    errorMessage: `This field should be at most ${phoneMaxLength} characters long`
                  },
                }}
              />
            </AvGroup>
          </Col>
        </Row>
        <Row>
          <Col md="12">
            <AvGroup>
              <Label for="age">Age</Label>
              <AvField
                type="number"
                name="age"
                id="age"
                value={ageInput}
                onChange={handlePatientChange}
                placeholder="Enter the mother's age"
                required
                validate={{
                  min: {value: MIN_AGE, errorMessage: `Age must be at least ${MIN_AGE} years`},
                  max: {value: MAX_AGE, errorMessage: `Age must be at most ${MAX_AGE} years`},
                  number: {errorMessage: 'Please enter a valid number'}
                }}
              />
            </AvGroup>
          </Col>
        </Row>
        {patientCase?.primaryContactType !== ContactRelation.OTHER
          && patientCase?.emergencyType !== 'NEONATAL' && patientCase?.emergencyType !== 'POSTPARTUM' && (
            <>
              <Row>
                <Col md="12">
                  <AvGroup>
                    <Label for="gestationalAge">Gestational Age (weeks)</Label>
                    <AvField
                      type="number"
                      name="gestationalAge"
                      id="gestationalAge"
                      value={patient?.gestationalAge || ''}
                      onChange={handlePatientChange}
                      placeholder="Enter gestational age in weeks"
                      required
                      validate={{
                        min: {value: 0, errorMessage: 'Gestational age must be at least 0'},
                        max: {value: 99, errorMessage: 'Gestational age must be at most 99'},
                        number: {errorMessage: 'Please enter a valid number'}
                      }}
                    />
                  </AvGroup>
                </Col>
              </Row>
              <Row>
                <Col md="12">
                  <AvGroup>
                    <Label for="gravida">Gravida</Label>
                    <AvField
                      type="number"
                      name="gravida"
                      id="gravida"
                      value={patient?.gravida || ''}
                      onChange={handlePatientChange}
                      placeholder="Number of pregnancies"
                      required
                      validate={{
                        min: {value: 0, errorMessage: 'Gravida must be at least 0'},
                        max: {value: 99, errorMessage: 'Gravida must be at most 99'},
                        number: {errorMessage: 'Please enter a valid number'}
                      }}
                    />
                  </AvGroup>
                </Col>
                <Col md="12">
                  <AvGroup>
                    <Label for="para">Para</Label>
                    <AvField
                      type="number"
                      name="para"
                      id="para"
                      value={patient?.para || ''}
                      onChange={handlePatientChange}
                      placeholder="Number of births"
                      required
                      validate={{
                        min: {value: 0, errorMessage: 'Para must be at least 0'},
                        max: {value: 99, errorMessage: 'Para must be at most 99'},
                        number: {errorMessage: 'Please enter a valid number'}
                      }}
                    />
                  </AvGroup>
                </Col>
              </Row>
            </>
          )}
        <RegistrationButtons
          currentStep={props.currentStep}
          handlePreviousStep={props.handlePreviousStep}
        />
      </AvForm>
    </div>
  );
};

const mapStateToProps = (state: IRootState) => ({
  patient: state.registration.entity.patient,
  patientCase: state.registration.entity.patientCase,
  locationType: state.registration.entity.locationType,
});

const mapDispatchToProps = {
  setRegistrationData,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(PatientInfo);
